import { useEffect, useState } from "react";
import { useAppSelector } from "../../../context-store";
import IdNameModel from "../../../models/common/IdNameModel";
import PagedListModel from "../../../models/common/PagedListModel";
import CityResponseModel from "../../../models/admin/city/response/CityResponseModel";
import { PagingEnum } from "../../../models/enums/IAIEnum";
import CitySearchInputModel from "../../../models/admin/city/request/CitySearchInputModel";
import toasterUtils from "../../../utils/toaster-utils";
import masterDataService from "../../../services/master-data-service";
import modalUtils from "../../../utils/modal-utils";
import cityService from "../../../services/admin/admin-city-service";
import { GeneralPoupConfig, ModalSize } from "../../../models/modal-types";
import CityRequestModel from "../../../models/admin/city/request/CityRequestModel";
import CityCeateComponent from "./CityCeateComponent";
import { SortDirection } from "../../../models/enums/SortDirectionEnum";
import { PageModeEnum } from "../../../models/enums/PageModeEnum";
import CityGridComponent from "./CityGridComponent";
import { Autocomplete, Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

interface CityProps {
    countryList: IdNameModel[];
}

const CityComponent = (props: CityProps) => {
    let loggedInUser = useAppSelector((state) => state.authState.loggedInUser);
    const [cityPagedList, setCityPagedList] = useState<PagedListModel<CityResponseModel>>();
    const [cityList, setCityList] = useState<CityResponseModel[]>([]);
    const [refreshCity, setRefreshCity] = useState<number>(2);
    const [countryFilter, setCountryFilter] = useState<number>(0);
    const [page, setPage] = useState<number>(PagingEnum.page);
    const [pageSize, setPageSize] = useState<number>(PagingEnum.pageSize);
    const [sortColumn, setSortColumn] = useState<string>("CityName");
    const [sortDirection, setSortDirection] = useState<number>(PagingEnum.sortDirection);
    const [pageSizesList, setPageSizesList] = useState<number[]>([]);

    useEffect(() => {
        loadPageSizes();
        getCities(getSearchFilters(countryFilter, page, pageSize, sortColumn, sortDirection));
    }, []);

    const loadPageSizes = () => {
        masterDataService.loadPageSizesForPaging().then((res) => {
            setPageSizesList(res);
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    const getSearchFilters = (countryFilter: number, page: number, pageSize: number, sortColumn: string, sortDirection: number) => {
        var inputData: CitySearchInputModel = {
            countryId: countryFilter,
            page: page,
            pageSize: pageSize,
            sort: sortColumn,
            sortDir: sortDirection
        };
        return inputData;
    };

    const getCities = (inputData: CitySearchInputModel) => {
        setRefreshCity(2);
        modalUtils.showLoader();
        cityService.getCities(inputData).then((res) => {
            modalUtils.closeModal();
            setCityList([]);
            setCityPagedList(res);
            setCityList(res.items);
            setRefreshCity(refreshCity * 5);
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onCityActionHandler = (obj: CityRequestModel, actionType: string) => {
        openCitySaveModel(obj, actionType);
    };

    const openCitySaveModel = (cityRequestModel: CityRequestModel, actionType: string) => {
        const config: GeneralPoupConfig = {
            title: "Add City",
            content: (
                <CityCeateComponent
                    objCity={cityRequestModel}
                    countryList={props.countryList}
                    actionType={actionType}
                    onCloseCity={onCloseCityHandler}
                    onCitySave={onCitySaveHandler}
                />
            ),
            size: ModalSize.LG,
            className: "model-min-height-500"
        };
        modalUtils.showPopup(config);
    };

    const onCloseCityHandler = () => {
        modalUtils.closeModal();
    };

    const onCitySaveHandler = (objcity: CityRequestModel) => {
        modalUtils.showLoader();
        cityService.saveCity(objcity).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            toasterUtils.showSuccess(res.informationMessages.toString());
            getCities(getSearchFilters(countryFilter, page, pageSize, sortColumn, sortDirection));
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onPageChangeActionHandler = (newPage: number) => {
        setPage(newPage);
        getCities(getSearchFilters(countryFilter, newPage, pageSize, sortColumn, sortDirection));
    };

    const onPageSizeChangeActionHandler = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(PagingEnum.page);
        getCities(getSearchFilters(countryFilter, PagingEnum.page, newPageSize, sortColumn, sortDirection));
    };

    const onSortActionHandler = (newSortColumn: string, sortDir: string) => {
        setSortColumn(newSortColumn);
        setSortDirection(sortDir === "asc" ? SortDirection.Asc : SortDirection.Desc);
        getCities(getSearchFilters(countryFilter, page, pageSize, newSortColumn, sortDir === "asc" ? SortDirection.Asc : SortDirection.Desc));
    };

    const addCity = () => {
        if (loggedInUser !== null) {
            let cityRequestModel: CityRequestModel = {
                cityId: 0,
                cityName: "",
                description: "",
                countryId: 0,
                userId: loggedInUser?.userId
            }
            onCityActionHandler(cityRequestModel, PageModeEnum.Create);
        }
    };

    return (
        <div className="card card-white">
            <div className="card-head">
                <div className="row">
                    <div className="col-md-6">
                        <div className="row">
                            <div className="col-md-6">
                                <FormControl fullWidth margin="none" size="small">
                                    <Autocomplete options={props.countryList} value={props.countryList.find((x) => x.id === countryFilter) || null}
                                        onChange={(_, newValue) => setCountryFilter(newValue ? newValue.id : 0)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.id === value.id}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Country" size="small" margin="normal" fullWidth required />
                                        )}
                                    />
                                </FormControl>
                            </div>
                            <div className="col-md-6">
                                <Button variant="contained" color="success" onClick={() => getCities(getSearchFilters(countryFilter, page, pageSize, sortColumn, sortDirection))} startIcon={<SearchIcon />} sx={{ ml: 2, mt: 2 }}>
                                    Search
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6 add-user-link">
                        <a className="btn-grid-add-action" title="Reload Data" onClick={() => getCities(getSearchFilters(countryFilter, page, pageSize, sortColumn, sortDirection))}>
                            <i className="fa fa-refresh"></i>
                        </a>
                        <a className="btn-grid-add-action" onClick={addCity}> + Add City</a>
                    </div>
                </div>
            </div>
            <div className="card-body">
                {cityList.length > 0 && refreshCity > 2 &&
                    <CityGridComponent
                        key={refreshCity}
                        cityList={cityList}
                        totalSize={cityPagedList?.totalCount ? cityPagedList?.totalCount : cityList.length}
                        currentPage={page + 1}
                        pagesize={pageSize}
                        sortColumn={sortColumn}
                        sortDirection={sortDirection === SortDirection.Asc ? "asc" : "desc"}
                        pageSizesList={pageSizesList}
                        onCityAction={onCityActionHandler}
                        onPageChangeAction={onPageChangeActionHandler}
                        onPageSizeChangeAction={onPageSizeChangeActionHandler}
                        onSortAction={onSortActionHandler} />
                }
            </div>
        </div>
    );
};
export default CityComponent;